import React from 'react';
import { Vehicle } from '../../types';
import { formatPrice } from '../../utils/dateUtils';

interface VehicleDetailModalProps {
  vehicle: Vehicle | null;
  isOpen: boolean;
  onClose: () => void;
  onSelect: () => void;
}

const VehicleDetailModal: React.FC<VehicleDetailModalProps> = ({
  vehicle,
  isOpen,
  onClose,
  onSelect,
}) => {
  if (!isOpen || !vehicle) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b p-4 flex items-center justify-between">
          <h2 className="text-2xl font-bold">{vehicle.model_name}</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Image */}
          <div className="mb-6">
            <img
              src={vehicle.image_url || '/placeholder-car.svg'}
              alt={vehicle.model_name}
              className="w-full h-64 object-cover rounded-lg"
            />
          </div>

          {/* Specifications */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-4">차량 사양</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center">
                <span className="text-gray-600 mr-2">차종:</span>
                <span className="font-medium">{vehicle.vehicle_type}</span>
              </div>
              <div className="flex items-center">
                <span className="text-gray-600 mr-2">연식:</span>
                <span className="font-medium">{vehicle.year}년</span>
              </div>
              <div className="flex items-center">
                <span className="text-gray-600 mr-2">승차인원:</span>
                <span className="font-medium">{vehicle.capacity}인승</span>
              </div>
              <div className="flex items-center">
                <span className="text-gray-600 mr-2">연료:</span>
                <span className="font-medium">{vehicle.fuel_type}</span>
              </div>
              <div className="flex items-center">
                <span className="text-gray-600 mr-2">변속기:</span>
                <span className="font-medium">{vehicle.transmission}</span>
              </div>
              <div className="flex items-center">
                <span className="text-gray-600 mr-2">상태:</span>
                <span className={`font-medium ${
                  vehicle.status === 'available' ? 'text-green-600' : 'text-red-600'
                }`}>
                  {vehicle.status === 'available' ? '예약 가능' : '예약 불가'}
                </span>
              </div>
            </div>
          </div>

          {/* Features */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-4">포함 옵션</h3>
            <div className="grid grid-cols-2 gap-2">
              <div className="flex items-center text-sm text-gray-700">
                <svg className="w-5 h-5 mr-2 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                네비게이션
              </div>
              <div className="flex items-center text-sm text-gray-700">
                <svg className="w-5 h-5 mr-2 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                블랙박스
              </div>
              <div className="flex items-center text-sm text-gray-700">
                <svg className="w-5 h-5 mr-2 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                후방카메라
              </div>
              <div className="flex items-center text-sm text-gray-700">
                <svg className="w-5 h-5 mr-2 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                보험 포함
              </div>
            </div>
          </div>

          {/* Pricing */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <h3 className="text-lg font-semibold mb-3">대여 요금</h3>
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">시간당</span>
                <span className="text-xl font-bold text-primary-600">
                  {formatPrice(vehicle.price_per_hour || vehicle.hourly_rate || 50000)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">일일 (24시간)</span>
                <span className="text-xl font-bold text-primary-600">
                  {formatPrice(vehicle.price_per_day || (vehicle.hourly_rate || 50000) * 24)}
                </span>
              </div>
            </div>
          </div>

          {/* Terms */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-3">대여 조건</h3>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• 만 21세 이상, 운전면허 취득 1년 이상</li>
              <li>• 신분증 및 운전면허증 필수</li>
              <li>• 예약 시간 기준 최소 1시간 전 취소 가능</li>
              <li>• 차량 인수/반납은 영업소에서만 가능</li>
            </ul>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t p-4 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-lg transition-colors"
          >
            닫기
          </button>
          <button
            onClick={() => {
              onSelect();
              onClose();
            }}
            className="flex-1 px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
          >
            이 차량 선택하기
          </button>
        </div>
      </div>
    </div>
  );
};

export default VehicleDetailModal;
